import React from 'react';
import * as Sentry from '@sentry/react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop/types';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled } from '@components-shared/src/stitches';
import { Slider } from '@components/basics/Slider';
import { Spinner } from '@components/basics/Spinner';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { StandardModalButton } from '@components/main-components/modals/StandardModalButton';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext } from '@context/modal';
import { ToastContext } from '@context/toast';
import { ASSUMED_HEIGHT_HEADER_FOOTER_MODAL_PX } from '@utils/constants';
import { getCroppedImage } from './helpers';

export const ImageCropModal = React.forwardRef<
  HTMLDivElement,
  {
    modalTitle?: string;
    forCover?: boolean;
    blobURL: string | null;
    setBlob: (b: Blob | null) => void;
    handleCloseAction?: () => void;
    handleCancelAction?: () => void;
    saveImageMutation?: (file: File) => Promise<void>;
    closeOnUpload?: boolean;
    handleReselectImage?: () => void;
    preventClose?: boolean;
  }
>(function ImageCropModal(
  {
    modalTitle,
    blobURL,
    forCover = false,
    setBlob,
    handleCloseAction,
    handleCancelAction,
    saveImageMutation,
    closeOnUpload = false,
    handleReselectImage,
    preventClose = false,
  },
  ref,
) {
  const { openToast } = ToastContext.useContainer();
  const { tablet } = MediaQuery.useContainer();
  const [loading, setLoading] = React.useState(false);
  const { closeCancelModal } = ModalContext.useContainer();
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area>();

  const onCropComplete = (_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCancelCrop = useStableCallback(() => {
    setBlob(null);
    handleCancelAction?.();
    closeCancelModal();
  });

  const handleSave = useStableCallback(async () => {
    if (!croppedAreaPixels || !blobURL) {
      return;
    }

    const croppedImage = await getCroppedImage({ imageSrc: blobURL, crop: croppedAreaPixels });
    setBlob(croppedImage);

    if (saveImageMutation && croppedImage) {
      const newImage = new File([croppedImage], 'newImage.png', { type: 'image/png' });

      if (closeOnUpload) {
        saveImageMutation(newImage);
      } else {
        setLoading(true);
        await saveImageMutation(newImage).catch(err => {
          Sentry.captureException(err);
          // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
          openToast({ text: 'Something went wrong', variant: 'error' });
        });
      }
    }

    closeCancelModal();
    handleCloseAction?.();
  });

  const body = loading ? (
    <CropperContainer
      forCover={forCover}
      css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      ref={ref}
    >
      <Spinner />
    </CropperContainer>
  ) : (
    <>
      <CropperContainer forCover={forCover} ref={ref}>
        <Cropper
          image={blobURL || ''}
          crop={crop}
          zoom={zoom}
          // smallest possible aspect ratio for cover
          aspect={forCover ? 2 / 1 : 1}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          objectFit={forCover ? 'horizontal-cover' : 'contain'}
          style={{ cropAreaStyle: { aspectRatio: '2/1' } }}
        />
      </CropperContainer>
      <SliderContainer>
        <Slider
          aria-label="slider-zoom-image"
          value={zoom}
          onChange={ev => setZoom(ev.target.valueAsNumber)}
        />
      </SliderContainer>
    </>
  );

  const reselectImageButton = handleReselectImage && (
    <StandardModalButton
      label="Browse Image"
      variant="secondary"
      size="M"
      onClick={handleReselectImage}
      isNotModalSmall
    />
  );

  const saveButton = (
    <StandardModalButton
      label="Save"
      variant="primary"
      size="M"
      onClick={handleSave}
      loading={loading}
      isNotModalSmall
    />
  );

  return (
    <StandardModal
      title={!!modalTitle ? modalTitle : 'Edit Image'}
      subHeader={
        tablet ? 'Drag the handle to resize the image.' : 'Drag your finger to resize the image.'
      }
      body={body}
      closeModal={!preventClose ? handleCancelCrop : undefined}
      size="large"
      backCallToAction={reselectImageButton ?? null}
      callToAction={saveButton}
      preventClose={preventClose}
    />
  );
});

const CropperContainer = styled('div', {
  position: 'relative',
  marginTop: 16,
  height: 308.31,
  '@tablet': {
    marginTop: 0,
    // Make sure cropper is always totally visible, else it seems like the image itself is cropped
    height: `calc(100vh - ${ASSUMED_HEIGHT_HEADER_FOOTER_MODAL_PX}px)`,
    maxHeight: 458,
  },

  variants: {
    forCover: {
      true: {
        marginTop: 0,
        width: '100%',
        height: 'unset',
        aspectRatio: '2/1',
        '@tablet': {
          height: '100%',
        },
      },
    },
  },
});

const SliderContainer = styled('div', {
  marginTop: 16,
  '@tablet': {
    marginTop: 24,
  },
});
